import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Calendar, RefreshCw, GripVertical, AlertCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  in_progress: "#059669",
  queued: "#0284c7",
  paused: "#d97706",
  planned: "#6366f1",
  completed: "#94a3b8",
};

const PRIORITY_STRIPE = { 5: "#dc2626", 4: "#ea580c", 3: "#f59e0b", 2: "#0284c7", 1: "#64748b" };

// Escala: pixels por minuto
const PX_PER_MIN_MAP = { 1: 2.5, 3: 1.2, 7: 0.55, 14: 0.28, 30: 0.14 };
const ROW_HEIGHT = 56;
const HEADER_HEIGHT = 46;
const LABEL_WIDTH = 180;

export default function Gantt() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(null); // {orderId, offsetPx}

  const pxPerMin = PX_PER_MIN_MAP[days] || 0.5;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/gantt/schedule?days=${days}`);
      setData(r.data);
    } catch (e) { toast.error("Erro a carregar Gantt"); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const windowStart = useMemo(() => data ? new Date(data.window_start) : new Date(), [data]);
  const windowEnd = useMemo(() => data ? new Date(data.window_end) : new Date(), [data]);
  const totalMinutes = useMemo(() => Math.max(1, (windowEnd - windowStart) / 60000), [windowStart, windowEnd]);
  const totalWidth = totalMinutes * pxPerMin;

  // Marcações de tempo — divide em intervalos consoante zoom
  const timeMarks = useMemo(() => {
    if (!data) return [];
    const marks = [];
    const startMs = windowStart.getTime();
    const stepMin = days <= 1 ? 60 : days <= 3 ? 240 : days <= 7 ? 720 : 1440;
    for (let t = 0; t <= totalMinutes; t += stepMin) {
      marks.push({
        left: t * pxPerMin,
        label: new Date(startMs + t * 60000).toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }),
      });
    }
    return marks;
  }, [data, windowStart, totalMinutes, pxPerMin, days]);

  const nowLeft = useMemo(() => {
    const nowMs = Date.now();
    const minsFromStart = (nowMs - windowStart.getTime()) / 60000;
    return minsFromStart * pxPerMin;
  }, [windowStart, pxPerMin]);

  const onDragStart = (e, order) => {
    e.dataTransfer.effectAllowed = "move";
    setDragging({ orderId: order.id, order });
    try { e.dataTransfer.setData("text/plain", order.id); } catch (_) {}
  };

  const onDropOnMachine = async (e, machine) => {
    e.preventDefault();
    if (!dragging) return;
    // calcula tempo com base no offset do drop dentro da row
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const minsFromStart = Math.max(0, x / pxPerMin);
    const newStart = new Date(windowStart.getTime() + minsFromStart * 60000).toISOString();
    try {
      await axios.patch(`${API}/gantt/order/${dragging.orderId}`, {
        machine_id: machine?.id || null,
        planned_start: newStart,
      });
      toast.success(`Ordem movida para ${machine?.code || "backlog"}`);
      setDragging(null);
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro a mover"); }
  };

  const onDropOnBacklog = async (e) => {
    e.preventDefault();
    if (!dragging) return;
    try {
      await axios.patch(`${API}/gantt/order/${dragging.orderId}`, { machine_id: "" });
      toast.success("Ordem devolvida ao backlog");
      setDragging(null);
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-4" data-testid="gantt-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Calendar className="h-8 w-8 text-indigo-600" /> Gantt de Produção
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Planeamento visual por máquina. Arrasta as ordens para reagendar ou trocar de máquina.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="gantt-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 dia</SelectItem>
              <SelectItem value="3">3 dias</SelectItem>
              <SelectItem value="7">1 semana</SelectItem>
              <SelectItem value="14">2 semanas</SelectItem>
              <SelectItem value="30">30 dias</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData} data-testid="gantt-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
        </div>
      </div>

      {loading && !data && <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin" /></div>}

      {data && (
        <div className="bg-white border rounded-lg overflow-hidden">
          {/* Scroll horizontal container */}
          <div className="overflow-x-auto">
            <div style={{ width: LABEL_WIDTH + totalWidth, minWidth: "100%" }}>
              {/* Header timeline */}
              <div className="flex sticky top-0 z-20 bg-slate-50 border-b" style={{ height: HEADER_HEIGHT }}>
                <div className="flex-shrink-0 border-r bg-slate-100 font-semibold text-xs flex items-center px-3" style={{ width: LABEL_WIDTH }}>
                  Máquina
                </div>
                <div className="relative flex-1" style={{ width: totalWidth }}>
                  {timeMarks.map((m, i) => (
                    <div key={i} className="absolute top-0 h-full flex items-center border-l text-[10px] text-slate-500 pl-1 whitespace-nowrap"
                         style={{ left: m.left, width: 1 }}>
                      <span className="ml-1">{m.label}</span>
                    </div>
                  ))}
                  {/* NOW line */}
                  {nowLeft >= 0 && nowLeft <= totalWidth && (
                    <div className="absolute top-0 h-full border-l-2 border-red-500 z-10" style={{ left: nowLeft }}>
                      <span className="text-[10px] bg-red-500 text-white px-1 rounded-br">AGORA</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Machine rows */}
              {data.machines.map((m) => (
                <div key={m.id} className="flex border-b hover:bg-slate-50/40 group">
                  <div className="flex-shrink-0 border-r bg-white px-3 py-2 flex flex-col justify-center" style={{ width: LABEL_WIDTH, height: ROW_HEIGHT }}>
                    <div className="text-sm font-semibold text-slate-900">{m.code}</div>
                    <div className="text-[11px] text-slate-500 truncate">{m.name}</div>
                  </div>
                  <div
                    className="relative flex-1"
                    style={{ width: totalWidth, height: ROW_HEIGHT }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => onDropOnMachine(e, m)}
                    data-testid={`gantt-machine-row-${m.code}`}
                  >
                    {/* Vertical grid */}
                    {timeMarks.map((mk, i) => (
                      <div key={i} className="absolute top-0 h-full border-l border-slate-100" style={{ left: mk.left }} />
                    ))}
                    {/* NOW line */}
                    {nowLeft >= 0 && nowLeft <= totalWidth && (
                      <div className="absolute top-0 h-full border-l border-red-400 opacity-40 z-0" style={{ left: nowLeft }} />
                    )}
                    {/* Order blocks */}
                    {m.orders.map((o) => {
                      const startMs = new Date(o.start).getTime();
                      const endMs = new Date(o.end).getTime();
                      const leftPx = Math.max(0, (startMs - windowStart.getTime()) / 60000 * pxPerMin);
                      const widthPx = Math.max(6, (endMs - startMs) / 60000 * pxPerMin);
                      const color = STATUS_COLORS[o.status] || "#6366f1";
                      const stripe = PRIORITY_STRIPE[o.priority] || "#64748b";
                      return (
                        <div
                          key={o.id}
                          draggable
                          onDragStart={(e) => onDragStart(e, o)}
                          className={`absolute top-1 rounded-md shadow-sm text-white text-[11px] px-2 py-1 cursor-move overflow-hidden ${o.late ? "ring-2 ring-red-500" : ""}`}
                          style={{ left: leftPx, width: widthPx, height: ROW_HEIGHT - 8, backgroundColor: color, borderLeft: `4px solid ${stripe}` }}
                          title={`${o.order_number} — ${o.part_code}\n${o.client || ""}\n${o.qty} peças · início ${new Date(o.start).toLocaleString("pt-PT")}\n${o.duration_min} min`}
                          data-testid={`gantt-order-${o.id}`}
                        >
                          <div className="flex items-center gap-1 font-semibold truncate">
                            <GripVertical className="h-3 w-3 opacity-70 flex-shrink-0" />
                            <span className="truncate">{o.order_number}</span>
                          </div>
                          <div className="truncate opacity-90">{o.part_code} · {o.qty}</div>
                          {o.late && <AlertCircle className="h-3 w-3 absolute top-1 right-1 text-yellow-200" />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Legenda */}
          <div className="flex flex-wrap items-center gap-4 px-4 py-2 bg-slate-50 border-t text-xs text-slate-600">
            <span className="font-semibold">Estados:</span>
            {Object.entries(STATUS_COLORS).map(([k, c]) => (
              <span key={k} className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: c }} />{k}</span>
            ))}
            <span className="ml-4 font-semibold">Prioridade:</span>
            {[5,4,3,2,1].map(p => <span key={p} className="flex items-center gap-1"><span className="inline-block w-1 h-3" style={{ backgroundColor: PRIORITY_STRIPE[p] }} />P{p}</span>)}
            <span className="ml-4"><span className="inline-block w-3 h-3 rounded ring-2 ring-red-500" /> Atrasada</span>
          </div>
        </div>
      )}

      {/* Backlog */}
      {data && data.backlog.length > 0 && (
        <div
          className="bg-white border rounded-lg p-4"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDropOnBacklog}
          data-testid="gantt-backlog"
        >
          <h2 className="font-semibold mb-3 text-slate-800">Backlog — Ordens sem máquina ({data.backlog.length})</h2>
          <div className="flex flex-wrap gap-2">
            {data.backlog.map(o => (
              <div
                key={o.id}
                draggable
                onDragStart={(e) => onDragStart(e, o)}
                className="border rounded-md px-3 py-2 text-xs cursor-move hover:shadow-md hover:border-indigo-400 transition bg-slate-50"
                data-testid={`gantt-backlog-${o.id}`}
              >
                <div className="font-semibold flex items-center gap-1"><GripVertical className="h-3 w-3 text-slate-400" />{o.order_number}</div>
                <div className="text-slate-600">{o.part_code} · {o.qty}</div>
                <div className="text-slate-400 text-[10px]">{Math.round(o.duration_min)} min</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
